import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation } from "react-router";
import { useSession } from "@/api/onboarding";
import { handleAccessError } from "@/auth/access";
import { setCurrentTenant, signOut } from "@/auth/session";
import { ScreenLoading } from "@/app/ScreenLoading";
import { useTheme } from "@/app/theme-context";
import styles from "@/components/shell/shell.module.css";
import bar from "@/components/shell/tabbar.module.css";
import { PHONE, useMediaQuery } from "@/components/shell/useMediaQuery";
import { Glyph } from "@/components/ui/Glyph";
import { LogoTile } from "@/components/ui/Logo";
import type { GlyphName } from "@/lib/glyphs";
import own from "./platform.module.css";

/**
 * The platform console's frame: the Midnight rail with its own screens (tenants, the team and
 * the platform's audit log) and a way back into a workspace. It reads only platform data, so a superadmin who belongs
 * to no tenant can still use it.
 */

const NAV: { to: string; label: string; glyph: GlyphName; match: (p: string) => boolean }[] = [
  {
    to: "/platform",
    label: "Tenants",
    glyph: "hub",
    match: (p) => p === "/platform" || p.startsWith("/platform/tenants"),
  },
  { to: "/platform/team", label: "Team", glyph: "shield", match: (p) => p === "/platform/team" },
  {
    to: "/platform/audit",
    label: "Audit",
    glyph: "history",
    match: (p) => p === "/platform/audit",
  },
];

function RailLink({
  to,
  label,
  glyph,
  current,
}: {
  to: string;
  label: string;
  glyph: GlyphName;
  current: boolean;
}) {
  return (
    <Link to={to} className={styles.item} aria-current={current ? "page" : undefined} title={label}>
      <span className={styles.tile}>
        <Glyph name={glyph} size={20} strokeWidth={1.8} />
      </span>
      <span className={styles.label}>{label}</span>
    </Link>
  );
}

function Account() {
  const session = useSession();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const data = session.data;
  const initials = (data?.name ?? "")
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] ?? "")
    .join("")
    .toUpperCase();
  return (
    <div ref={ref} className={styles.account}>
      <button
        type="button"
        className={`${styles.avatar} zh-press`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={data ? `${initials}, account: ${data.name}, superadmin` : "Account"}
        onClick={() => setOpen((o) => !o)}
      >
        {initials}
      </button>
      {open && data ? (
        <div role="menu" aria-label="Account" className={`zh-glass ${styles.menu}`}>
          <div className={styles.menuWho}>
            <div className={styles.menuName}>{data.name}</div>
            <div className={styles.menuSub}>Superadmin, {data.email}</div>
          </div>
          {data.tenants.map((t) => (
            <button
              key={t.slug}
              type="button"
              role="menuitem"
              className={styles.menuItem}
              onClick={() => openWorkspace(t.slug)}
            >
              <span>Open {t.name}</span>
            </button>
          ))}
          <button
            type="button"
            role="menuitem"
            className={styles.menuItem}
            onClick={() => void signOut()}
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

function openWorkspace(slug: string | null) {
  setCurrentTenant(slug);
  window.location.assign("/");
}

/** On a phone the rail becomes a bar along the bottom, as it does in a workspace. */
function PhoneBar({ pathname, workspace }: { pathname: string; workspace: string | null }) {
  const tabs = workspace ? NAV.length + 1 : NAV.length;
  return (
    <nav
      aria-label="Platform"
      className={bar.bar}
      style={{ gridTemplateColumns: `repeat(${tabs}, minmax(0, 1fr))` }}
    >
      {NAV.map((n) => (
        <Link
          key={n.to}
          to={n.to}
          className={bar.tab}
          aria-current={n.match(pathname) ? "page" : undefined}
        >
          <span className={bar.icon}>
            <Glyph name={n.glyph} size={21} strokeWidth={1.9} />
          </span>
          <span className={bar.label}>{n.label}</span>
        </Link>
      ))}
      {workspace ? (
        <button type="button" className={bar.tab} onClick={() => openWorkspace(workspace)}>
          <span className={bar.icon}>
            <Glyph name="home" size={21} strokeWidth={1.9} />
          </span>
          <span className={bar.label}>Workspace</span>
        </button>
      ) : null}
    </nav>
  );
}

export function PlatformShell() {
  const session = useSession();
  const { pathname } = useLocation();
  const { theme, toggle } = useTheme();
  const phone = useMediaQuery(PHONE);

  useEffect(() => {
    if (session.isError) handleAccessError(session.error);
  }, [session.isError, session.error]);

  if (session.isPending) return <ScreenLoading />;
  if (session.data && !session.data.is_superadmin) {
    return (
      <div className={styles.app}>
        <div className={styles.column}>
          <main id="main" tabIndex={-1} className={own.gate}>
            <div className={own.gateCard}>
              <LogoTile size={46} />
              <h1 className={own.gateTitle}>The platform console is for superadmins</h1>
              <p className={own.gateText}>
                It's where the ZeaOps team creates tenants and invites their first admins. Your
                workspace has everything you need.
              </p>
              <Link to="/">Back to your workspace</Link>
            </div>
          </main>
        </div>
      </div>
    );
  }
  const tenants = session.data?.tenants ?? [];
  const workspace = tenants[0]?.slug ?? null;
  return (
    <div className={styles.app}>
      {phone ? null : (
        <div className={styles.rail}>
          <nav aria-label="Platform" className={styles.nav}>
            <Link to="/platform" aria-label="ZeaOps platform" className={styles.logo}>
              <LogoTile size={46} />
            </Link>
            {NAV.map((n) => (
              <RailLink
                key={n.to}
                to={n.to}
                label={n.label}
                glyph={n.glyph}
                current={n.match(pathname)}
              />
            ))}
          </nav>
          {tenants.length ? (
            <nav aria-label="Workspaces" className={`${styles.nav} ${styles.navBottom}`}>
              <button
                type="button"
                className={`${styles.item} ${own.railButton}`}
                title={`Open ${tenants[0]?.name ?? "your workspace"}`}
                onClick={() => openWorkspace(workspace)}
              >
                <span className={styles.tile}>
                  <Glyph name="home" size={20} strokeWidth={1.8} />
                </span>
                <span className={styles.label}>Workspace</span>
              </button>
            </nav>
          ) : null}
        </div>
      )}
      <div className={styles.column}>
        <header className={styles.topbar}>
          <h1 className={styles.heading}>Platform</h1>
          <span className={styles.env} title="The platform console">
            <span className={styles.envDot} style={{ background: "var(--violet)" }} />
            ZeaOps platform console
          </span>
          <span className={styles.grow} />
          <button
            type="button"
            className={`${styles.round} zh-press`}
            aria-label={theme === "dark" ? "Use light theme" : "Use dark theme"}
            onClick={toggle}
          >
            <Glyph name={theme === "dark" ? "sun" : "moon"} size={17} />
          </button>
          <Account />
        </header>
        <div className={styles.body}>
          <main id="main" tabIndex={-1} className={styles.main}>
            <Outlet />
          </main>
        </div>
        {phone ? <PhoneBar pathname={pathname} workspace={workspace} /> : null}
      </div>
    </div>
  );
}
